import { useEffect } from "react";
import "@/index.css";
import "@/i18n";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { useTranslation } from "react-i18next";

import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingWhatsApp from "@/components/site/FloatingWhatsApp";
import { SUPPORTED_LANGS, DEFAULT_LANG, detectLangFromPath } from "@/i18n";

import Home from "@/pages/Home";
import ExperiencesPage from "@/pages/ExperiencesPage";
import ExperienceDetail from "@/pages/ExperienceDetail";
import ApuseniPage from "@/pages/ApuseniPage";
import Booking from "@/pages/Booking";
import Contact from "@/pages/Contact";
import HolidaysPage from "@/pages/HolidaysPage";
import BookingTermsPage from "@/pages/BookingTermsPage";
import { HOLIDAYS_PATH, TERMS_PATH } from "@/data/holidays";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);
  return null;
}

// Static no-JS SEO fallbacks from public/index.html are superseded by per-route <SEO> tags.
function RemoveStaticSeoFallbacks() {
  useEffect(() => {
    document.querySelectorAll("head [data-seo-fallback]").forEach((el) => el.remove());
  }, []);
  return null;
}

// Syncs i18next language with the current URL prefix. Runs on every navigation.
function LangSync() {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  useEffect(() => {
    const lang = detectLangFromPath(pathname);
    if (lang !== i18n.language) i18n.changeLanguage(lang);
    try { localStorage.setItem("apuseni_lang", lang); } catch { /* noop */ }
  }, [pathname, i18n]);
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <Header />
      <main>{children}</main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}

// Build a route set that mounts every public route on both `/` (RO default)
// AND on `/:lang` (en, de, hu). Admin routes stay language-agnostic.
export function AppRoutes() {
  const publicRoutes = [
    { path: "", element: <Home /> },
    { path: "experiente", element: <ExperiencesPage /> },
    { path: "experiente/:slug", element: <ExperienceDetail /> },
    { path: "apuseni", element: <ApuseniPage /> },
    { path: "rezervare", element: <Booking /> },
    { path: "contact", element: <Contact /> },
    { path: HOLIDAYS_PATH.slice(1), element: <HolidaysPage /> },
    { path: TERMS_PATH.slice(1), element: <BookingTermsPage /> },
  ];

  return (
    <Routes>
      {/* Default (Romanian) — no prefix */}
      {publicRoutes.map((r) => (
        <Route
          key={"ro-" + r.path}
          path={"/" + r.path}
          element={<PublicLayout>{r.element}</PublicLayout>}
        />
      ))}

      {/* Prefixed languages */}
      {SUPPORTED_LANGS.filter((l) => l !== DEFAULT_LANG).map((l) =>
        publicRoutes.map((r) => (
          <Route
            key={l + "-" + r.path}
            path={`/${l}/${r.path}`}
            element={<PublicLayout>{r.element}</PublicLayout>}
          />
        ))
      )}

      {/* Admin */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <ScrollToTop />
            <RemoveStaticSeoFallbacks />
            <LangSync />
            <Toaster position="top-center" richColors theme="light" />
            <AppRoutes />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
